import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useUser } from '@clerk/clerk-react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { CryptoWallet, CustomToken, CryptoTransaction, CreateWalletRequest, CreateTokenRequest, TransferTokenRequest } from '../../../../shared/types'

export const CryptoWalletManager: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [wallets, setWallets] = useState<CryptoWallet[]>([])
  const [tokens, setTokens] = useState<CustomToken[]>([])
  const [transactions, setTransactions] = useState<CryptoTransaction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'wallets' | 'tokens' | 'trading' | 'staking'>('wallets')
  const [showCreateWallet, setShowCreateWallet] = useState(false)
  const [showCreateToken, setShowCreateToken] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState<string>('')

  const [newWallet, setNewWallet] = useState({
    wallet_name: ''
  })

  const [newToken, setNewToken] = useState({
    token_name: '',
    token_symbol: '',
    token_description: '',
    total_supply: '',
    initial_price: '',
    token_logo_url: ''
  })

  const [transferData, setTransferData] = useState({
    to_wallet_address: '',
    token_id: '',
    amount: '',
    transaction_fee: '0'
  })

  useEffect(() => {
    console.log('useEffect triggered, user:', user, 'user?.username:', user?.username)
    if (user?.username) {
      console.log('Loading data for user:', user.username)
      loadWallets()
      loadTokens()
    } else {
      console.log('No username available, using fallback')
      // Even if no user, try to load with fallback
      loadWallets()
      loadTokens()
    }
  }, [user?.username])

  // Additional useEffect to handle user loading
  useEffect(() => {
    console.log('User state changed:', user)
    if (user && user.username) {
      console.log('User loaded successfully:', user.username)
      loadWallets()
      loadTokens()
    }
  }, [user])

  useEffect(() => {
    if (selectedWallet) {
      loadTransactions()
    }
  }, [selectedWallet])

  const loadWallets = async () => {
    setIsLoading(true)
    try {
      const playerId = user?.username || 'player_1'
      console.log('Loading wallets for playerId:', playerId, 'user:', user)
      const response = await fetch(`https://business-tycoon-worker.smnshzh.workers.dev/api/crypto/wallets/${playerId}`)
      const data = await response.json()
      
      if (data.success) {
        setWallets(data.data || [])
      } else {
        console.error('Failed to load wallets:', data.error)
        setWallets([])
      }
    } catch (error) {
      console.error('Error loading wallets:', error)
      setWallets([])
    } finally {
      setIsLoading(false)
    }
  }

  const loadTokens = async () => {
    try {
      const playerId = user?.username || 'player_1'
      const response = await fetch(`https://business-tycoon-worker.smnshzh.workers.dev/api/crypto/tokens/player/${playerId}`)
      const data = await response.json()
      
      if (data.success) {
        setTokens(data.data || [])
      } else {
        console.error('Failed to load tokens:', data.error)
        setTokens([])
      }
    } catch (error) {
      console.error('Error loading tokens:', error)
      setTokens([])
    }
  }

  const loadTransactions = async () => {
    try {
      const response = await fetch(`https://business-tycoon-worker.smnshzh.workers.dev/api/crypto/wallets/${selectedWallet}/transactions`)
      const data = await response.json()
      
      if (data.success) {
        setTransactions(data.data || [])
      } else {
        console.error('Failed to load transactions:', data.error)
        setTransactions([])
      }
    } catch (error) {
      console.error('Error loading transactions:', error)
      setTransactions([])
    }
  }

  const handleCreateWallet = async () => {
    if (!newWallet.wallet_name.trim()) {
      alert(t('crypto.fillRequiredFields'))
      return
    }

    setIsLoading(true)
    try {
      const playerId = user?.username || 'player_1'
      console.log('Creating wallet for playerId:', playerId, 'user:', user)
      const walletData: CreateWalletRequest = {
        wallet_name: newWallet.wallet_name,
        player_id: playerId
      }

      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/crypto/wallets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(walletData),
      })

      const data = await response.json()
      
      if (data.success) {
        setWallets([...wallets, data.data])
        setShowCreateWallet(false)
        setNewWallet({ wallet_name: '' })
        alert(t('crypto.walletCreated'))
      } else {
        alert(t('crypto.walletError') + ': ' + data.error)
      }
    } catch (error) {
      console.error('Error creating wallet:', error)
      alert(t('crypto.walletError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateToken = async () => {
    if (!newToken.token_name.trim() || !newToken.token_symbol.trim() || !newToken.total_supply.trim()) {
      alert(t('crypto.fillRequiredFields'))
      return
    }

    setIsLoading(true)
    try {
      const tokenData: CreateTokenRequest = {
        token_name: newToken.token_name,
        token_symbol: newToken.token_symbol,
        token_description: newToken.token_description || '',
        total_supply: parseInt(newToken.total_supply),
        initial_price: parseFloat(newToken.initial_price) || 0,
        creator_player_id: user?.username || 'player_1'
      }

      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/crypto/tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tokenData),
      })

      const data = await response.json()
      
      if (data.success) {
        setTokens([...tokens, data.data])
        setShowCreateToken(false)
        setNewToken({
          token_name: '',
          token_symbol: '',
          token_description: '',
          total_supply: '',
          initial_price: '',
          token_logo_url: ''
        })
        alert(t('crypto.tokenCreated'))
      } else {
        alert(t('crypto.tokenError') + ': ' + data.error)
      }
    } catch (error) {
      console.error('Error creating token:', error)
      alert(t('crypto.tokenError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleTransfer = async () => {
    if (!transferData.to_wallet_address.trim() || !transferData.token_id || !transferData.amount.trim()) {
      alert(t('crypto.fillRequiredFields'))
      return
    }

    setIsLoading(true)
    try {
      const transferRequest: TransferTokenRequest = {
        from_wallet_id: selectedWallet,
        to_wallet_address: transferData.to_wallet_address,
        token_id: transferData.token_id,
        amount: parseFloat(transferData.amount),
        transaction_fee: parseFloat(transferData.transaction_fee) || 0
      }

      const response = await fetch('https://business-tycoon-worker.smnshzh.workers.dev/api/crypto/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transferRequest),
      })

      const data = await response.json()
      
      if (data.success) {
        setShowTransfer(false)
        setTransferData({
          to_wallet_address: '',
          token_id: '',
          amount: '',
          transaction_fee: '0'
        })
        loadTransactions()
        alert(t('crypto.transferSuccess'))
      } else {
        alert(t('crypto.transferError') + ': ' + data.error)
      }
    } catch (error) {
      console.error('Error transferring tokens:', error)
      alert(t('crypto.transferError'))
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fa-IR')
  }

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card title={`💼 ${t('crypto.title')}`} subtitle={t('crypto.subtitle')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{wallets.length}</div>
            <div className="text-sm text-gray-600">{t('crypto.wallets')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{tokens.length}</div>
            <div className="text-sm text-gray-600">{t('crypto.tokens')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{transactions.length}</div>
            <div className="text-sm text-gray-600">{t('crypto.transactions')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">0</div>
            <div className="text-sm text-gray-600">{t('crypto.staking')}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowCreateWallet(true)}
            variant="primary"
            size="sm"
          >
            {t('crypto.createWallet')}
          </Button>
          <Button
            onClick={() => setShowCreateToken(true)}
            variant="secondary"
            size="sm"
          >
            {t('crypto.createToken')}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex space-x-1 space-x-reverse bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('wallets')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'wallets'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          💼 {t('crypto.wallets')}
        </button>
        <button
          onClick={() => setActiveTab('tokens')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'tokens'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🪙 {t('crypto.tokens')}
        </button>
        <button
          onClick={() => setActiveTab('trading')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'trading'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📈 {t('crypto.transactions')}
        </button>
        <button
          onClick={() => setActiveTab('staking')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'staking'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          🔒 {t('crypto.staking')}
        </button>
      </div>

      {/* Wallets Tab */}
      {activeTab === 'wallets' && (
        <Card title={t('crypto.yourWallets')} subtitle={t('crypto.walletsSubtitle')}>
          {isLoading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : wallets.length > 0 ? (
            <div className="space-y-4">
              {wallets.map((wallet) => (
                <div key={wallet.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">{wallet.wallet_name}</h3>
                      <p className="text-sm text-gray-600">{t('crypto.address')}: {formatAddress(wallet.wallet_address)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">
                        {wallet.balance?.toLocaleString() || '0'} {t('common.price')}
                      </p>
                      <p className="text-sm text-gray-500">{t('crypto.balance')}</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      {t('crypto.createdAt')}: {formatDate(wallet.created_at)}
                    </div>
                    <div className="flex space-x-2 space-x-reverse">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSelectedWallet(wallet.id)
                          setActiveTab('trading')
                        }}
                      >
                        {t('crypto.viewDetails')}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          setSelectedWallet(wallet.id)
                          setShowTransfer(true)
                        }}
                      >
                        {t('crypto.transfer')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💼</span>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                {t('crypto.noWallets')}
              </h3>
              <p className="text-gray-500 mb-4">
                {t('crypto.noWalletsMessage')}
              </p>
              <Button variant="primary" onClick={() => setShowCreateWallet(true)}>
                {t('crypto.createWallet')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Tokens Tab */}
      {activeTab === 'tokens' && (
        <Card title={t('crypto.yourTokens')} subtitle={t('crypto.tokensSubtitle')}>
          {tokens.length > 0 ? (
            <div className="space-y-4">
              {tokens.map((token) => (
                <div key={token.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">{token.token_name}</h3>
                      <p className="text-sm text-gray-600">{t('crypto.symbol')}: {token.token_symbol}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">
                        {token.token_price?.toLocaleString() || '0'} {t('common.price')}
                      </p>
                      <p className="text-sm text-gray-500">{t('crypto.price')}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-sm text-gray-600">{t('crypto.supply')}</p>
                      <p className="font-medium">{token.total_supply?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('crypto.marketCap')}</p>
                      <p className="font-medium">
                        {((token.token_price || 0) * (token.total_supply || 0)).toLocaleString()} {t('common.price')}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      {t('crypto.createdAt')}: {formatDate(token.created_at)}
                    </div>
                    <div className="flex space-x-2 space-x-reverse">
                      <Button size="sm" variant="secondary">
                        {t('crypto.buy')}
                      </Button>
                      <Button size="sm" variant="primary">
                        {t('crypto.sell')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🪙</span>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                {t('crypto.noTokens')}
              </h3>
              <p className="text-gray-500 mb-4">
                {t('crypto.noTokensMessage')}
              </p>
              <Button variant="primary" onClick={() => setShowCreateToken(true)}>
                {t('crypto.createToken')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Trading Tab */}
      {activeTab === 'trading' && (
        <Card title={t('crypto.yourTransactions')} subtitle={t('crypto.transactionsSubtitle')}>
          {selectedWallet ? (
            <div className="space-y-4">
              {transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium text-gray-800">
                          {transaction.transaction_type === 'transfer' ? t('crypto.transfer') : transaction.transaction_type}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {t('crypto.amount')}: {transaction.amount}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          transaction.transaction_type === 'transfer' && transaction.from_wallet_id === selectedWallet
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}>
                          {transaction.transaction_type === 'transfer' && transaction.from_wallet_id === selectedWallet ? '-' : '+'}
                          {transaction.amount}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(transaction.created_at)}</p>
                      </div>
                    </div>
                    {transaction.transaction_fee > 0 && (
                      <p className="text-sm text-gray-500">
                        {t('crypto.transactionFee')}: {transaction.transaction_fee} {t('common.price')}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">{t('crypto.noTransactionsMessage')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">{t('crypto.selectWalletFirst')}</p>
            </div>
          )}
        </Card>
      )}

      {/* Staking Tab */}
      {activeTab === 'staking' && (
        <Card title={t('crypto.stakingPools')} subtitle={t('crypto.stakingSubtitle')}>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔒</span>
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t('crypto.stakingComingSoon')}
            </h3>
            <p className="text-gray-500">
              {t('crypto.stakingDescription')}
            </p>
          </div>
        </Card>
      )}

      {/* Create Wallet Modal */}
      {showCreateWallet && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <Card title={t('crypto.createWallet')} subtitle={t('crypto.createWalletSubtitle')} className="w-full max-w-md">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.walletName')} *
                </label>
                <input
                  type="text"
                  value={newWallet.wallet_name}
                  onChange={(e) => setNewWallet({ ...newWallet, wallet_name: e.target.value })}
                  className="input-field"
                  placeholder={t('crypto.walletNamePlaceholder')}
                />
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse">
                <Button variant="secondary" onClick={() => setShowCreateWallet(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleCreateWallet} disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('crypto.createWallet')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Create Token Modal */}
      {showCreateToken && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <Card title={t('crypto.createToken')} subtitle={t('crypto.createTokenSubtitle')} className="w-full max-w-2xl">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('crypto.tokenName')} *
                  </label>
                  <input
                    type="text"
                    value={newToken.token_name}
                    onChange={(e) => setNewToken({ ...newToken, token_name: e.target.value })}
                    className="input-field"
                    placeholder={t('crypto.tokenNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('crypto.tokenSymbol')} *
                  </label>
                  <input
                    type="text"
                    value={newToken.token_symbol}
                    onChange={(e) => setNewToken({ ...newToken, token_symbol: e.target.value.toUpperCase() })}
                    className="input-field"
                    placeholder={t('crypto.tokenSymbolPlaceholder')}
                    maxLength={10}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.tokenDescription')}
                </label>
                <textarea
                  value={newToken.token_description}
                  onChange={(e) => setNewToken({ ...newToken, token_description: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder={t('crypto.tokenDescriptionPlaceholder')}
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('crypto.totalSupply')} *
                  </label>
                  <input
                    type="number"
                    value={newToken.total_supply}
                    onChange={(e) => setNewToken({ ...newToken, total_supply: e.target.value })}
                    className="input-field"
                    placeholder={t('crypto.totalSupplyPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('crypto.initialPrice')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newToken.initial_price}
                    onChange={(e) => setNewToken({ ...newToken, initial_price: e.target.value })}
                    className="input-field"
                    placeholder={t('crypto.initialPricePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.tokenLogoUrl')}
                </label>
                <input
                  type="url"
                  value={newToken.token_logo_url}
                  onChange={(e) => setNewToken({ ...newToken, token_logo_url: e.target.value })}
                  className="input-field"
                  placeholder={t('crypto.tokenLogoUrlPlaceholder')}
                />
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse">
                <Button variant="secondary" onClick={() => setShowCreateToken(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleCreateToken} disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('crypto.createToken')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <Card title={t('crypto.transfer')} subtitle={t('crypto.transferSubtitle')} className="w-full max-w-md">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.recipient')} *
                </label>
                <input
                  type="text"
                  value={transferData.to_wallet_address}
                  onChange={(e) => setTransferData({ ...transferData, to_wallet_address: e.target.value })}
                  className="input-field"
                  placeholder={t('crypto.recipientPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.selectToken')} *
                </label>
                <select
                  value={transferData.token_id}
                  onChange={(e) => setTransferData({ ...transferData, token_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">{t('crypto.selectToken')}</option>
                  {tokens.map((token) => (
                    <option key={token.id} value={token.id}>
                      {token.token_name} ({token.token_symbol})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.amount')} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                  className="input-field"
                  placeholder={t('crypto.amountPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('crypto.transactionFee')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={transferData.transaction_fee}
                  onChange={(e) => setTransferData({ ...transferData, transaction_fee: e.target.value })}
                  className="input-field"
                  placeholder="0"
                />
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse">
                <Button variant="secondary" onClick={() => setShowTransfer(false)}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleTransfer} disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('crypto.transfer')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}